/**
 * Compact role-profile card for the role detail sheets (Role Master and the
 * Organisation page). Shows where the profile stands and opens the full dialog.
 *
 * The Organisation page's role drawer is also shown to people without Role
 * Master access; for them the profile API answers 403 and this card renders
 * nothing rather than an error.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Download, FileText, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import roleProfileService, { type RoleProfileBundle } from '@/services/api/roleProfileService';
import { RoleProfileDialog, downloadBlob, profileFilename } from './RoleProfileDialog';

export const RoleProfileCard: React.FC<{ roleId: number }> = ({ roleId }) => {
  const [bundle, setBundle] = useState<RoleProfileBundle | null>(null);
  const [hidden, setHidden] = useState(false);
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    try {
      setBundle(await roleProfileService.getRole(roleId));
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (status === 401 || status === 403) setHidden(true);
    }
  }, [roleId]);

  useEffect(() => { setBundle(null); setHidden(false); load(); }, [load]);

  if (hidden) return null;
  const a = bundle?.approved;
  const o = bundle?.open;
  const shown = a ?? o;

  const download = async () => {
    if (!bundle || !shown) return;
    setDownloading(true);
    try {
      downloadBlob(await roleProfileService.exportDocx(roleId), profileFilename(bundle.role.title, shown.version, !a));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="rounded border bg-gray-50 p-3">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="flex items-center gap-1.5 text-sm font-medium text-gray-700"><FileText className="h-4 w-4" />Role profile</h4>
        {!bundle && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
      </div>
      {bundle && (
        <>
          <div className="flex flex-wrap items-center gap-1.5 text-sm">
            {a && <Badge className="bg-emerald-100 text-emerald-800">Approved v{a.version}</Badge>}
            {o && <Badge className={o.status === 'submitted' ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'}>
              {o.status === 'submitted' ? 'Awaiting approval' : 'Draft'} v{o.version}</Badge>}
            {!a && !o && <span className="text-gray-500">No profile yet</span>}
            {shown && (
              <span className="ml-auto flex items-center gap-2 text-xs text-gray-600">
                <Progress value={shown.completeness.score} className="h-1.5 w-20" />{shown.completeness.score}%
              </span>
            )}
          </div>
          <div className="mt-2 flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setOpen(true)}>{shown ? 'Open profile' : 'Write profile'}</Button>
            {shown && (
              <Button size="sm" variant="ghost" onClick={download} disabled={downloading}>
                {downloading ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Download className="mr-1 h-4 w-4" />}Word
              </Button>
            )}
          </div>
        </>
      )}
      <RoleProfileDialog roleId={roleId} open={open} onOpenChange={setOpen} onChanged={load} />
    </div>
  );
};

export default RoleProfileCard;
